/**
 * Tab TikTok Shop (Open Platform chính chủ, platform 40).
 *
 * Backend đã có OAuth + webhook + gửi tin từ trước (modules/tiktok-shop) nhưng
 * màn Tích hợp chưa có chỗ nào hiện — shop kết nối rồi mà nhân viên không thấy,
 * không tắt/gỡ được. Tab này: cấu hình app key/secret, nút kết nối (uỷ quyền
 * trên TikTok), danh sách shop đã kết nối với tạm dừng / kết nối lại / gỡ.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { KeyRound, Loader2, Plus, ShoppingBag, Trash2, PauseCircle, PlayCircle, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Loading, ErrorState, EmptyState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { PLATFORM } from '@/lib/channel-groups'
import { useAuthStore } from '@/stores/auth-store'
import {
  useZaloAccounts, usePauseZaloAccount, useResumeZaloAccount, useDeleteZaloAccount,
  useTikTokShopConfig, useSaveTikTokShopConfig, useTikTokShopConnectUrl, statusMeta,
  type ChannelAccount,
} from '@/hooks/use-integrations'
import { ChannelCard } from './channel-card'
import { ReportSwitch } from './report-switch'

function CauHinhApp({ canEdit }: { canEdit: boolean }) {
  const cfg = useTikTokShopConfig()
  const save = useSaveTikTokShopConfig()
  const [appKey, setAppKey] = useState<string | null>(null)
  const [appSecret, setAppSecret] = useState('')
  const key = appKey ?? cfg.data?.appKey ?? ''

  const luu = () => {
    if (!key.trim()) { toast.error('Nhập App Key của ứng dụng TikTok Shop'); return }
    save.mutate({ appKey: key.trim(), ...(appSecret.trim() ? { appSecret: appSecret.trim() } : {}) }, {
      onSuccess: () => { toast.success('Đã lưu cấu hình TikTok Shop'); setAppSecret('') },
      onError: (e) => toast.error(apiError(e)),
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="h-4 w-4" />
          Ứng dụng TikTok Shop (Open Platform)
          {cfg.data?.isConfigured && <Badge variant="success" className="ml-1">Đã cấu hình</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Tạo ứng dụng tại <a className="text-primary underline" href="https://partner.tiktokshop.com" target="_blank" rel="noreferrer">partner.tiktokshop.com</a>,
          lấy App Key / App Secret và khai báo 2 địa chỉ dưới đây trong ứng dụng.
        </p>
        {cfg.data && (
          <div className="grid gap-2 rounded-md bg-muted/50 p-3 text-xs sm:grid-cols-2">
            <div><span className="text-muted-foreground">Redirect URL:</span> <code className="break-all">{cfg.data.redirectUri}</code></div>
            <div><span className="text-muted-foreground">Webhook URL:</span> <code className="break-all">{cfg.data.webhookUrl}</code></div>
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs">App Key</Label>
            <Input value={key} onChange={(e) => setAppKey(e.target.value)} disabled={!canEdit} placeholder="6abc…" />
          </div>
          <div>
            <Label className="text-xs">App Secret {cfg.data?.hasAppSecret && <span className="font-normal text-muted-foreground">(đang lưu: {cfg.data.appSecretMasked} — để trống nếu giữ nguyên)</span>}</Label>
            <Input type="password" value={appSecret} onChange={(e) => setAppSecret(e.target.value)} disabled={!canEdit} placeholder="••••••••" autoComplete="new-password" />
          </div>
        </div>
        <Button onClick={luu} disabled={!canEdit || save.isPending}>
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Lưu cấu hình
        </Button>
      </CardContent>
    </Card>
  )
}

export function TikTokShopTab() {
  const role = useAuthStore((s) => s.user?.role)
  const canEdit = role === 'owner' || role === 'admin'
  const { data, isLoading, isError } = useZaloAccounts(undefined, false, true)
  const cfg = useTikTokShopConfig()
  const ketNoi = useTikTokShopConnectUrl()
  const pause = usePauseZaloAccount()
  const resume = useResumeZaloAccount()
  const remove = useDeleteZaloAccount()
  const [toDelete, setToDelete] = useState<ChannelAccount | null>(null)

  const shops = (data ?? []).filter((a) => a.platform === PLATFORM.TIKTOK_SHOP)

  const batDauKetNoi = () =>
    ketNoi.mutate(undefined, {
      onSuccess: (r) => { window.location.href = r.authUrl },
      onError: (e) => toast.error(apiError(e)),
    })

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">TikTok Shop</h3>
          <p className="text-sm text-muted-foreground">
            Kết nối shop trên TikTok để nhận tin nhắn khách và trả lời ngay trong Hội thoại. Một ứng dụng kết nối được nhiều shop.
          </p>
        </div>
        <Button onClick={batDauKetNoi} disabled={!canEdit || ketNoi.isPending || !cfg.data?.isConfigured} title={!cfg.data?.isConfigured ? 'Lưu App Key / App Secret trước' : undefined}>
          {ketNoi.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Kết nối shop
        </Button>
      </div>

      <CauHinhApp canEdit={canEdit} />

      {isLoading ? (
        <Loading label="Đang tải danh sách shop..." />
      ) : isError ? (
        <ErrorState message="Không tải được danh sách shop TikTok" />
      ) : shops.length === 0 ? (
        <EmptyState
          icon={ShoppingBag}
          title="Chưa kết nối shop TikTok nào"
          description="Lưu cấu hình ứng dụng rồi bấm Kết nối shop — TikTok sẽ hỏi uỷ quyền bằng tài khoản người bán."
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {shops.map((acc) => {
            const st = statusMeta(acc.liveStatus ?? acc.status, acc.isDisabled)
            const song = !acc.isDisabled && (acc.liveStatus ?? acc.status) === 'connected'
            return (
              <ChannelCard
                key={acc.id}
                icon={<ShoppingBag className="h-5 w-5" />}
                iconClassName="bg-black text-white"
                avatarUrl={acc.avatarUrl}
                title={acc.displayName || 'Shop không tên'}
                subtitle={
                  <>
                    {acc.externalPageId && <span className="font-mono text-[11px]">shop {acc.externalPageId.slice(0, 18)}{acc.externalPageId.length > 18 ? '…' : ''} · </span>}
                    {acc.lastConnectedAt ? `Kết nối lần cuối ${dayjs(acc.lastConnectedAt).format('DD/MM/YYYY HH:mm')}` : 'Chưa từng kết nối'}
                  </>
                }
                status={st}
                actions={
                  <>
                    {!song && (
                      <Button variant="default" size="sm" onClick={batDauKetNoi} disabled={!canEdit || ketNoi.isPending} title="Uỷ quyền lại trên TikTok">
                        <ExternalLink /> Kết nối lại
                      </Button>
                    )}
                    {acc.isDisabled ? (
                      <Button variant="outline" size="sm" onClick={() => resume.mutate(acc.id, { onSuccess: () => toast.success('Đã bật lại shop'), onError: (e) => toast.error(apiError(e)) })} disabled={!canEdit || resume.isPending}>
                        <PlayCircle /> Bật lại
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => pause.mutate(acc.id, { onSuccess: () => toast.success('Đã tạm dừng shop'), onError: (e) => toast.error(apiError(e)) })} disabled={!canEdit || pause.isPending}>
                        <PauseCircle /> Tạm dừng
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setToDelete(acc)} disabled={!canEdit}>
                      <Trash2 /> Gỡ
                    </Button>
                  </>
                }
              >
                <ReportSwitch acc={acc} canEdit={canEdit} />
              </ChannelCard>
            )
          })}
        </div>
      )}

      <Dialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gỡ shop "{toDelete?.displayName}"?</DialogTitle>
            <DialogDescription>
              Shop rời khỏi danh sách và ngừng nhận tin. Hội thoại cũ vẫn giữ trong hệ thống. Muốn dùng lại phải uỷ quyền trên TikTok từ đầu.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToDelete(null)}>Huỷ</Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => toDelete && remove.mutate(toDelete.id, {
                onSuccess: () => { toast.success('Đã gỡ shop'); setToDelete(null) },
                onError: (e) => toast.error(apiError(e)),
              })}
            >
              Gỡ shop
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
